/**
 * Consent first (SPEC_V5 §0.5). Google's Privacy & messaging (Funding Choices)
 * is the certified CMP configured in Ad Manager. We load its tag for the
 * network, then wait for the IAB signals it exposes: TCF v2.2 (`__tcfapi`,
 * EEA/UK/CH) and GPP / USP (`__gpp`, `__uspapi`, US states). GPT reads these
 * strings itself; we additionally switch GPT to non-personalised ads when
 * consent for personalisation is missing, and restrict data processing on a
 * US "Do not sell or share" opt-out.
 */

export interface ConsentState {
  /** A CMP answered (or said consent doesn't apply here). */
  ready: boolean;
  source: 'tcf' | 'gpp' | 'usp' | 'none';
  /** Personalised ads allowed (TCF purposes 1, 3 and 4, or GDPR doesn't apply). */
  personalized: boolean;
  /** US opt-out of sale/sharing ⇒ GPT restrictDataProcessing. */
  restrictDataProcessing: boolean;
}

interface TcData {
  gdprApplies?: boolean;
  eventStatus?: 'tcloaded' | 'cmpuishown' | 'useractioncomplete' | string;
  listenerId?: number;
  purpose?: { consents?: Record<string, boolean> };
}
type TcfApi = (cmd: string, version: number, cb: (data: TcData, success: boolean) => void, param?: unknown) => void;
type GppApi = (cmd: string, cb: (data: { signalStatus?: string } | null, success: boolean) => void) => void;
type UspApi = (cmd: string, version: number, cb: (data: { uspString?: string } | null, success: boolean) => void) => void;

interface ConsentWindow {
  __tcfapi?: TcfApi;
  __gpp?: GppApi;
  __uspapi?: UspApi;
}

export const FUNDING_CHOICES_ORIGIN = 'https://fundingchoicesmessages.google.com';

/** Ad Manager networks use the network code in the tag URL; AdSense-style `pub-…` ids pass through. */
export function fundingChoicesSrc(networkCode: string): string {
  return `${FUNDING_CHOICES_ORIGIN}/i/${encodeURIComponent(networkCode.trim())}?ers=1`;
}

/** Google's documented "googlefcPresent" frame signal that accompanies the tag. */
function signalGooglefcPresent(): void {
  if ((window.frames as unknown as Record<string, unknown>)['googlefcPresent']) return;
  if (!document.body) {
    setTimeout(signalGooglefcPresent, 0);
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'width:0;height:0;border:none;z-index:-1000;left:-1000px;top:-1000px;display:none';
  iframe.name = 'googlefcPresent';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  document.body.appendChild(iframe);
}

export function injectFundingChoices(networkCode: string): void {
  const src = fundingChoicesSrc(networkCode);
  if (document.querySelector(`script[src="${src}"]`)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  s.dataset.popinAds = 'consent';
  document.head.appendChild(s);
  signalGooglefcPresent();
}

const NONE: ConsentState = { ready: false, source: 'none', personalized: false, restrictDataProcessing: false };

function uspOptOut(w: ConsentWindow): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof w.__uspapi !== 'function') return resolve(false);
    const t = setTimeout(() => resolve(false), 1000);
    try {
      w.__uspapi('getUSPData', 1, (d, ok) => {
        clearTimeout(t);
        // "1YYN": 3rd character is the opt-out of sale.
        resolve(!!ok && typeof d?.uspString === 'string' && d.uspString.charAt(2).toUpperCase() === 'Y');
      });
    } catch {
      clearTimeout(t);
      resolve(false);
    }
  });
}

function waitForTcf(api: TcfApi, decideTimeoutMs: number): Promise<{ answered: boolean; personalized: boolean }> {
  return new Promise((resolve) => {
    let done = false;
    let listenerId: number | undefined;
    const finish = (r: { answered: boolean; personalized: boolean }) => {
      if (done) return;
      done = true;
      clearTimeout(t);
      if (listenerId !== undefined) {
        try {
          api('removeEventListener', 2, () => undefined, listenerId);
        } catch {
          /* ignore */
        }
      }
      resolve(r);
    };
    const t = setTimeout(() => finish({ answered: false, personalized: false }), decideTimeoutMs);
    try {
      api('addEventListener', 2, (d, success) => {
        if (!success || !d) return;
        listenerId = d.listenerId ?? listenerId;
        if (d.gdprApplies === false) return finish({ answered: true, personalized: true });
        if (d.eventStatus === 'tcloaded' || d.eventStatus === 'useractioncomplete') {
          const c = d.purpose?.consents ?? {};
          finish({ answered: true, personalized: !!(c['1'] && c['3'] && c['4']) });
        }
        // 'cmpuishown': the person is deciding — keep waiting.
      });
    } catch {
      finish({ answered: false, personalized: false });
    }
  });
}

function waitForGpp(api: GppApi): Promise<boolean> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(false), 2000);
    try {
      api('ping', (d) => {
        clearTimeout(t);
        resolve(!!d && (d.signalStatus === undefined || d.signalStatus === 'ready'));
      });
    } catch {
      clearTimeout(t);
      resolve(false);
    }
  });
}

/** Poll until a CMP API appears (the Funding Choices tag defines them). */
function waitForApi(appearTimeoutMs: number): Promise<ConsentWindow | null> {
  const w = window as unknown as ConsentWindow;
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (typeof w.__tcfapi === 'function' || typeof w.__gpp === 'function' || typeof w.__uspapi === 'function') return resolve(w);
      if (Date.now() - started >= appearTimeoutMs) return resolve(null);
      setTimeout(tick, 100);
    };
    tick();
  });
}

let pending: Promise<ConsentState> | null = null;

/**
 * Loads the CMP once per page and resolves with its answer. `ready: false`
 * means no CMP answered (blocked, offline, dev); callers decide what that means
 * (test/dev ⇒ non-personalised; production ⇒ no ads).
 */
export function obtainConsent(networkCode: string, opts: { appearTimeoutMs?: number; decideTimeoutMs?: number } = {}): Promise<ConsentState> {
  if (pending) return pending;
  pending = (async (): Promise<ConsentState> => {
    try {
      injectFundingChoices(networkCode);
    } catch {
      return NONE;
    }
    const w = await waitForApi(opts.appearTimeoutMs ?? 5000);
    if (!w) return NONE;
    const restrict = await uspOptOut(w);
    if (typeof w.__tcfapi === 'function') {
      const r = await waitForTcf(w.__tcfapi, opts.decideTimeoutMs ?? 120_000);
      return { ready: r.answered, source: 'tcf', personalized: r.answered && r.personalized && !restrict, restrictDataProcessing: restrict };
    }
    if (typeof w.__gpp === 'function') {
      const ok = await waitForGpp(w.__gpp);
      return { ready: ok, source: 'gpp', personalized: ok && !restrict, restrictDataProcessing: restrict };
    }
    return { ready: true, source: 'usp', personalized: !restrict, restrictDataProcessing: restrict };
  })();
  return pending;
}

/** Tests only. */
export function __resetConsent(): void {
  pending = null;
}
